<div class="observations-sync-results-modal">
  <!-- Modal Header -->
  <div class="modal-header">
    <h2 mat-dialog-title class="modal-title">
      <mat-icon class="title-icon" svgIcon="assignment"></mat-icon>
      {{ 'integration.observations_sync_results' | transloco }}
    </h2>
    <button
      mat-icon-button
      class="close-button"
      (click)="onClose()"
      [attr.aria-label]="'common.close' | transloco"
    >
      <mat-icon svgIcon="close"></mat-icon>
    </button>
  </div>

  <!-- Modal Content: summary stays put, the failed list scrolls -->
  <div mat-dialog-content class="modal-content">
    <div class="summary">
      <!-- Outcome banner -->
      <div
        class="alert"
        role="status"
        [ngClass]="{
          'alert-success': outcome === 'success',
          'alert-warning': outcome === 'warning',
          'alert-error': outcome === 'error'
        }"
      >
        <mat-icon
          class="alert-icon"
          [svgIcon]="
            outcome === 'success' ? 'check_circle' : outcome === 'warning' ? 'warning' : 'error'
          "
        ></mat-icon>
        <div class="alert-content">
          <ng-container *ngIf="outcome === 'success'">
            <h4>{{ 'integration.sync_completed' | transloco }}</h4>
            <p>{{ 'integration.observations_sync_completed_successfully' | transloco }}</p>
          </ng-container>
          <ng-container *ngIf="outcome === 'warning'">
            <h4>
              {{
                'integration.sync_completed_with_failures'
                  | transloco: { count: syncResult.failed_count }
              }}
            </h4>
            <p>{{ 'integration.sync_completed_with_failures_hint' | transloco }}</p>
          </ng-container>
          <ng-container *ngIf="outcome === 'error'">
            <h4>{{ 'integration.observations_sync_failed' | transloco }}</h4>
            <p>
              {{
                syncResult.message || ('integration.observations_sync_failed_message' | transloco)
              }}
            </p>
          </ng-container>
        </div>
      </div>

      <!-- Statistics -->
      <div class="stats-grid">
        <div class="stat-card created">
          <div class="stat-number">{{ syncResult.created_count | number }}</div>
          <div class="stat-label">{{ 'integration.observations_created' | transloco }}</div>
        </div>
        <div class="stat-card updated">
          <div class="stat-number">{{ syncResult.updated_count | number }}</div>
          <div class="stat-label">{{ 'integration.observations_updated' | transloco }}</div>
        </div>
        <div class="stat-card matched">
          <div class="stat-number">{{ syncResult.matched_count | number }}</div>
          <div class="stat-label">{{ 'integration.observations_matched' | transloco }}</div>
        </div>
        <div class="stat-card failed" [class.has-failures]="syncResult.failed_count > 0">
          <div class="stat-number">{{ syncResult.failed_count | number }}</div>
          <div class="stat-label">{{ 'integration.observations_failed' | transloco }}</div>
        </div>
      </div>

      <!-- Success rate -->
      <div class="success-rate">
        <div class="rate-text">
          <strong>{{ successRate | number: '1.0-1' }}%</strong>
          {{
            'integration.sync_succeeded_of'
              | transloco
                : {
                    ok: (succeededCount | number),
                    total: (totalCount | number)
                  }
          }}
        </div>
        <mat-progress-bar
          mode="determinate"
          [value]="successRate"
          [attr.aria-label]="'integration.success_rate' | transloco"
        ></mat-progress-bar>
      </div>
    </div>

    <!-- Failed transactions, grouped by reason -->
    <div *ngIf="hasFailedObservations()" class="failed-observations-section">
      <h3 class="failed-observations-title">
        {{
          'integration.sync_failed_transactions_title'
            | transloco: { count: syncResult.failed_count }
        }}
      </h3>

      <div class="failed-observations-list">
        <section
          *ngFor="let group of failedGroups; trackBy: trackByKey; let i = index"
          class="failed-group"
          [class.open]="expandedGroups.has(group.key)"
        >
          <button
            type="button"
            class="group-header"
            [id]="'sync-group-header-' + i"
            [attr.aria-expanded]="expandedGroups.has(group.key)"
            [attr.aria-controls]="'sync-group-panel-' + i"
            (click)="toggleGroup(group.key)"
          >
            <span class="group-count">{{ group.items.length }}</span>
            <span class="group-text">
              <span class="group-title">
                {{ group.actionKey ? (group.actionKey | transloco) : group.rawReason }}
              </span>
              <span *ngIf="group.actionKey" class="group-raw">
                {{ 'integration.sync_raw_message' | transloco }}: {{ group.rawReason }}
              </span>
            </span>
            <mat-icon class="group-chevron" svgIcon="heroicons_solid:chevron-down"></mat-icon>
          </button>

          <div
            *ngIf="expandedGroups.has(group.key)"
            class="group-records"
            role="region"
            [id]="'sync-group-panel-' + i"
            [attr.aria-labelledby]="'sync-group-header-' + i"
          >
            <div *ngFor="let project of group.projects" class="project-block">
              <div class="project-row">
                <span *ngIf="project.label" class="project-label" [title]="project.label">
                  <span class="project-number" dir="ltr">{{ project.number }}</span>
                  <span *ngIf="project.name" class="project-name" dir="auto">{{
                    project.name
                  }}</span>
                </span>
                <button type="button" class="copy-button" (click)="copyRecords(group, project)">
                  <mat-icon
                    class="copy-icon"
                    [svgIcon]="
                      copiedKey === group.key + '|' + project.label
                        ? 'heroicons_solid:check'
                        : 'mat_outline:content_copy'
                    "
                  ></mat-icon>
                  {{
                    (copiedKey === group.key + '|' + project.label
                      ? 'integration.sync_copied'
                      : 'integration.sync_copy_records'
                    ) | transloco
                  }}
                </button>
              </div>
              <div class="record-chips">
                <span *ngFor="let item of project.items" class="record-chip">
                  {{ item.recordId || '-' }}
                  <ng-container *ngIf="!group.actionKey">
                    <span *ngFor="let f of item.fields" class="chip-extra">
                      · {{ f.key }}: {{ f.value }}
                    </span>
                  </ng-container>
                </span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>

  <!-- Modal Actions -->
  <div mat-dialog-actions class="modal-actions">
    <button mat-button class="cancel-button" (click)="onClose()">
      {{ 'common.close' | transloco }}
    </button>
    <button
      mat-raised-button
      color="primary"
      class="refresh-button"
      (click)="onRefreshObservationsData()"
    >
      <mat-icon svgIcon="refresh"></mat-icon>
      {{ 'integration.refresh_observations_data' | transloco }}
    </button>
  </div>
</div>
